import {FC, useState, useEffect, ChangeEvent} from 'react'
import {useDispatch} from 'react-redux'
import {useSelector} from 'hooks/redux'
import queryString from 'query-string'
import {useHistory, useLocation} from 'react-router-dom'

import {Section} from 'jbc-front/components/presenters/form/Section'
import {Label, Required} from 'jbc-front/components/presenters/form/Label'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import Button from 'jbc-front/components/Button'
import {Error} from 'jbc-front/components/presenters/form/Error'

import {useFetchEmployeeDependents} from 'hooks/api/employees/employeeDependents'
import {verify} from 'validators/index'
import {handleError} from 'libs/axiosErrorHandlers'
import {isAdminSelector} from 'libs/redux'

interface CheckboxDependent {
  id: number
  name: string
  checked: boolean
}

interface FormErrors {
  dependents: string[]
}

export const ChooseDependents: FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isAdmin = useSelector(isAdminSelector)
  const currentUser = useSelector((state) => state.session.currentUser)
  const {search, pathname} = useLocation()
  const isEmployeeInput = !isAdmin || pathname.includes('/mypage/')
  const employeeId = isEmployeeInput ? currentUser.employee?.id : queryString.parse(search)['employee_id']
  const [checkboxDependents, setCheckboxDependents] = useState<CheckboxDependent[]>([])
  const initialErrors = {dependents: []}
  const [errors, setErrors] = useState<FormErrors>({...initialErrors})

  const {isLoading, resource: dependents, errors: fetchError} = useFetchEmployeeDependents(employeeId as string)

  useEffect(() => {
    const getEmployeeDependents = async () => {
      if (!isLoading) {
        if (fetchError) {
          await handleError(fetchError, dispatch)
          return
        }

        if (dependents) {
          const newCheckboxDependents = dependents.map((dependent) => {
            return {
              id: dependent.id,
              name: `${dependent.last_name} ${dependent.first_name}`,
              checked: false,
            }
          })

          setCheckboxDependents(newCheckboxDependents)
        }
      }
    }

    getEmployeeDependents()
  }, [isLoading])

  // 扶養情報がチェックされているかを確認するvalidator
  const requireCheckValidator = (values: CheckboxDependent[]) => {
    const result = {isValid: false, message: '家族情報のいずれかにチェックを入れてください'}
    values.forEach((value) => {
      if (value.checked) {
        result.isValid = true
        result.message = ''
      }
    })

    return result
  }

  const handleSubmit = () => {
    const {isValid, messages} = verify(checkboxDependents, [requireCheckValidator])

    if (isValid) {
      // チェックのついた扶養のIDをqueryにして画面遷移
      const query = new URLSearchParams()
      checkboxDependents.forEach((checkboxDependent) => {
        if (checkboxDependent.checked) {
          query.append('dependentIds[]', String(checkboxDependent.id))
        }
      })
      const path = isEmployeeInput
        ? `/mypage/remove_dependents/employee_input?${query}`
        : `/remove_dependents/detail?employee_id=${employeeId}&${query}`

      history.push(path)
    } else {
      setErrors({dependents: messages})
    }
  }

  const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const newCheckboxDependents = checkboxDependents.map((dependent) => {
      return {
        ...dependent,
        checked: String(dependent.id) == e.target.id ? !dependent.checked : dependent.checked,
      }
    })
    setCheckboxDependents(newCheckboxDependents)
    setErrors({...initialErrors})
  }

  const checkErrors = () => {
    const errorMessages = Object.keys(errors).filter((key) => errors[key as keyof FormErrors].length > 0)
    return errorMessages.length > 0
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">削除する家族を選択</h1>
      </div>

      {checkboxDependents.length > 0 ? (
        <form>
          {checkErrors() && (
            <Section title="入力内容に誤りがあります" error>
              {errors.dependents.map((error) => (
                <Error key={error} error={error} />
              ))}
            </Section>
          )}
          <Section title="家族情報">
            <Label>
              家族から外れる人にチェックを入れてください
              <Spacer direction="x" size={4} />
              <Required />
            </Label>
            <Spacer size={8} />
            <ul>
              {checkboxDependents.map((dependent) => {
                return (
                  <li className="u-mb10" key={`dependent-checkbox-${dependent.id}`}>
                    <Checkbox id={String(dependent.id)} checked={dependent.checked} onChange={handleCheck}>
                      {dependent.name}
                    </Checkbox>
                  </li>
                )
              })}
            </ul>
          </Section>
          <div className="u-ta-c u-mt30">
            <Button primary onClick={handleSubmit}>
              次へ
            </Button>
          </div>
        </form>
      ) : (
        <>
          <Section title="家族情報">
            <p>家族情報が存在しません。</p>
          </Section>
        </>
      )}
    </>
  )
}
